import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  getPublicOffer,
  getPublicOfferVersion,
  getPublicOfferVersions,
  type OfferVersion,
} from "../services/api";
import { SpinnerIcon } from "../components/icons";
import PublicOfferPreview from "../components/PublicOfferPreview";
import Layout from "../components/Layout";
import { cookieService } from "../services/cookieService";
import { analyticsService } from "../services/analyticsService";
import { TemplateId } from "../components/offer-templates/BaseTemplate";
import OfferVersionSelector from "../components/OfferVersionSelector";

interface PublicOfferData {
  id: string;
  title: string;
  description?: string;
  clientName: string;
  clientEmail: string;
  clientPhone: string;
  checkInDate: string;
  checkOutDate: string;
  numberOfRooms: number;
  numberOfGuests: number;
  netValue: number;
  totalDiscount: number;
  publishedAt?: string;
  createdAt: string;
  hotel: {
    name: string;
    address?: string;
    phone?: string;
    email?: string;
    website?: string;
    brandKit?: {
      keyVisualUrl?: string;
      templateId?: TemplateId;
      colors?: {
        primary?: string;
        secondary?: string;
        accent?: string;
      };
    };
    offerSections?: {
      termsAndConditions?: {
        enabled: boolean;
        content?: string;
        position?: "before" | "after";
      };
    };
  };
  offerDays: Array<{
    id: string;
    date: string;
    dayNumber: number;
    planItems: Array<{
      id: string;
      quantity: number;
      unitPrice: number;
      finalPrice: number;
      customTitle?: string;
      notes?: string;
      timeFrom?: string;
      timeTo?: string;
      categoryOption?: {
        name: string;
        optionType: string;
      };
    }>;
  }>;
}

const PublicOffer: React.FC = () => {
  const { token, versionToken } = useParams<{
    token: string;
    versionToken?: string;
  }>();
  const [offer, setOffer] = useState<PublicOfferData | null>(null);
  const [versions, setVersions] = useState<OfferVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isEndingSession = useRef(false);

  useEffect(() => {
    const fetchOffer = async () => {
      if (!token) {
        setError("Brak tokenu oferty");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        let offerData;

        if (versionToken) {
          // Fetch specific version
          offerData = await getPublicOfferVersion(token, versionToken);
        } else {
          // Fetch latest published version
          offerData = await getPublicOffer(token);
        }

        setOffer(offerData);

        // Fetch all versions for the dropdown (public endpoint - no auth required)
        try {
          const versionsData = await getPublicOfferVersions(token);
          // Backend already filters to published versions only
          setVersions(versionsData);
        } catch (versionError) {
          console.error("Error fetching versions:", versionError);
          // Don't fail the whole page if versions can't be fetched
          setVersions([]);
        }
      } catch (err: any) {
        console.error("Error fetching public offer:", err);
        if (err.response?.status === 404) {
          setError("Oferta nie została znaleziona lub nie jest dostępna");
        } else {
          setError("Wystąpił błąd podczas ładowania oferty");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchOffer();
  }, [token, versionToken]);

  // Initialize analytics when offer is loaded or version changes
  useEffect(() => {
    const initializeAnalytics = async () => {
      console.log("[PublicOffer] Initializing analytics for offer:", offer?.id);

      // Wait if previous session is ending
      while (isEndingSession.current) {
        console.log("[PublicOffer] Waiting for previous session to end...");
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      // End previous session if active (important when switching versions)
      if (analyticsService.isSessionActive()) {
        console.log(
          "[PublicOffer] Ending previous session before starting new one"
        );
        isEndingSession.current = true;
        await analyticsService.endSession();
        isEndingSession.current = false;
        console.log("[PublicOffer] Previous session ended successfully");
      }

      if (offer && token) {
        console.log("[PublicOffer] Starting new analytics session");
        // Pass offer.id to tell analytics which specific version is being viewed
        const success = await analyticsService.startSession(token, offer.id);

        if (success) {
          console.log("[PublicOffer] Analytics session started successfully");
          // Track initial page view
          analyticsService.trackEvent({
            eventType: "view",
            eventTarget: "offer-page",
            eventData: {
              offerTitle: offer.title,
              timestamp: Date.now(),
            },
          });
        } else {
          console.log("[PublicOffer] Failed to start analytics session");
        }
      } else {
        console.log(
          "[PublicOffer] Missing offer or token, cannot start session"
        );
      }
    };

    if (offer) {
      initializeAnalytics();
    }

    // Cleanup on unmount
    return () => {
      const cleanup = async () => {
        if (analyticsService.isSessionActive()) {
          console.log("[PublicOffer] Cleanup: ending analytics session");
          isEndingSession.current = true;
          await analyticsService.endSession();
          isEndingSession.current = false;
          console.log("[PublicOffer] Cleanup: session ended");
        }
      };
      cleanup();
    };
  }, [offer, token, versionToken]);

  // Handle consent changes
  useEffect(() => {
    const unsubscribe = cookieService.onConsentChange(async (consent) => {
      if (consent && offer && token && analyticsService.isSessionActive()) {
        // Update analytics session with new consent status
        await analyticsService.updateConsent(consent.analytics);

        console.log(`Analytics consent changed: ${consent.analytics}`);
      }
    });

    return unsubscribe;
  }, [offer, token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <SpinnerIcon className="h-12 w-12 text-blue-600 mx-auto mb-4" />
          <p className="text-lg text-neutral-600">Ładowanie oferty...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-600 text-2xl">⚠️</span>
          </div>
          <h1 className="text-2xl font-bold text-neutral-900 mb-2">
            Błąd ładowania oferty
          </h1>
          <p className="text-neutral-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Spróbuj ponownie
          </button>
        </div>
      </div>
    );
  }

  if (!offer) {
    return null;
  }

  return (
    <Layout>
      <div
        className="font-sans"
        onClick={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;
          analyticsService.trackClick(
            (e.target as HTMLElement).tagName.toLowerCase(),
            x,
            y,
            "offer-page"
          );
        }}
      >
        <div className="max-w-4xl mx-auto">
          <OfferVersionSelector
            versions={versions}
            currentOfferId={offer.id}
            currentPublicToken={token!}
            currentPublishedDate={offer.publishedAt}
          />

          <PublicOfferPreview
            offer={offer}
            brandKit={offer.hotel.brandKit}
            termsAndConditions={offer.hotel.offerSections?.termsAndConditions}
            editMode={false}
          />
        </div>
      </div>
    </Layout>
  );
};

export default PublicOffer;
